"use client";

import { FormEvent, useRef, useState } from "react";
import ErrorMessage from "@/components/ui/ErrorMessage";

interface LoginFormProps {
  onSubmit: (identifier: string, password: string) => Promise<void>;
}

export default function LoginForm({ onSubmit }: LoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const submitLock = useRef(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitLock.current) return;
    // Fecha o teclado virtual no mobile para o overlay de carregamento centralizar
    // corretamente (elementos fixed se posicionam pela layout viewport, não pela
    // área visível reduzida pelo teclado).
    (document.activeElement as HTMLElement | null)?.blur();
    submitLock.current = true;
    setIsLoading(true);
    setError("");
    const fd = new FormData(e.currentTarget);
    const identifier = (fd.get("identifier") as string)?.trim() ?? "";
    const password = (fd.get("password") as string) ?? "";
    try {
      await onSubmit(identifier, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao fazer login");
    } finally {
      setIsLoading(false);
      submitLock.current = false;
    }
  };

  return (
    <form
      method="post"
      action="/login"
      onSubmit={handleSubmit}
      className={`relative w-full max-w-md rounded-xl bg-white p-6 shadow-lg sm:p-8 ${isLoading ? "pointer-events-none" : ""}`}
    >
      <h1 className="mb-6 text-center text-2xl font-bold text-gray-900">
        Acesse sua conta
      </h1>

      {isLoading && (
        <div
          className="pointer-events-none absolute inset-0 z-10 rounded-xl bg-white/60 backdrop-blur-[2px] transition-opacity"
          aria-hidden
        />
      )}

      {error && <div className="mb-4"><ErrorMessage message={error} /></div>}

      <div className="mb-4">
        <label htmlFor="identifier" className="mb-1 block text-sm font-medium text-gray-700">
          Login
        </label>
        <input
          type="text"
          id="identifier"
          name="identifier"
          required
          disabled={isLoading}
          autoComplete="username"
          placeholder="usuario ou email"
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-amber-primary/50 focus:outline-none focus:ring-2 focus:ring-amber-primary/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70"
        />
      </div>

      <div className="mb-6">
        <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
          Senha
        </label>
        <input
          type="password"
          id="password"
          name="password"
          required
          disabled={isLoading}
          autoComplete="current-password"
          placeholder="••••••••"
          className="mt-1 w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-amber-primary/50 focus:outline-none focus:ring-2 focus:ring-amber-primary/10 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70"
        />
      </div>

      <button
        type="submit"
        disabled={isLoading}
        aria-busy={isLoading}
        className="relative flex w-full min-h-[42px] items-center justify-center gap-2 rounded-lg bg-amber-cta py-2.5 text-sm font-semibold text-white transition-all hover:bg-amber-ctaHover disabled:cursor-not-allowed disabled:opacity-90"
      >
        {isLoading && (
          <span
            className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/35 border-t-white"
            aria-hidden
          />
        )}
        <span>{isLoading ? "Acessando…" : "Acessar"}</span>
      </button>
    </form>
  );
}
